// src/contexts/CommunicationContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { pb } from "../services/pocketbase";
import { useAuth } from "./AuthContext";
import { useOperator } from "./OperatorContext";
import { notificationService } from "../services/notificationService";
import { chatService } from "../services/chatService";
import { taskService } from "../services/taskService";
import { aiAssistantService } from "../services/aiAssistantService";
import { soundNotificationService } from "../services/soundNotificationService";
import { colaboradoresService } from "../services/colaboradoresService";

const CommunicationContext = createContext({});

export function useCommunication() {
  return useContext(CommunicationContext);
}

export function CommunicationProvider({ children }) {
  const { user, hasAccess } = useAuth();
  const { activeOperator } = useOperator();

  // --- DRAWER & UI STATES ---
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeDrawerTab, setActiveDrawerTab] = useState("chat"); // "chat" | "notifications" | "tasks" | "help"
  const [quickOrderNumber, setQuickOrderNumber] = useState(null);
  const [quickProductSku, setQuickProductSku] = useState(null);
  const [lightboxData, setLightboxData] = useState(null); // { url, name }
  const [newTaskModalOpen, setNewTaskModalOpen] = useState(false);
  const [isAiThinking, setIsAiThinking] = useState(false);

  const [selectedAiProviderId, setSelectedAiProviderId] = useState(null);

  // --- COUNTERS ---
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadByChannel, setUnreadByChannel] = useState({});
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [soundEnabled, setSoundEnabledState] = useState(() => soundNotificationService.isSoundEnabled());

  const toggleSound = () => {
    const next = soundNotificationService.toggleSound();
    setSoundEnabledState(next);
    return next;
  };

  // --- DATA LISTS ---
  const [channels, setChannels] = useState([]);
  const [dms, setDms] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);

  // Total de mensagens não lidas somadas apenas dos canais e DMs que este usuário participa
  const unreadChatCount = useMemo(() => {
    const validIds = new Set([...channels.map((c) => c.id), ...dms.map((d) => d.id)]);
    return Object.entries(unreadByChannel).reduce((sum, [chId, count]) => {
      if (!validIds.has(chId)) return sum;
      return sum + (Number(count) || 0);
    }, 0);
  }, [unreadByChannel, channels, dms]);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [operatorsList, setOperatorsList] = useState([]);
  const [teamsList, setTeamsList] = useState([]);
  const [currentUserTeam, setCurrentUserTeam] = useState(null);
  const [currentUserTeams, setCurrentUserTeams] = useState([]);

  // --- REFS ---
  const activeChannelRef = useRef(activeChannel);
  activeChannelRef.current = activeChannel;
  const channelsRef = useRef(channels);
  channelsRef.current = channels;
  const dmsRef = useRef(dms);
  dmsRef.current = dms;
  const selectedAiProviderRef = useRef(selectedAiProviderId);
  selectedAiProviderRef.current = selectedAiProviderId;
  const drawerOpenRef = useRef(drawerOpen);
  drawerOpenRef.current = drawerOpen;
  const activeDrawerTabRef = useRef(activeDrawerTab);
  activeDrawerTabRef.current = activeDrawerTab;

  // --- GERENCIAMENTO DE MENSAGENS NÃO LIDAS POR CANAL ---
  const getLastReadTimestamps = useCallback(() => {
    try {
      const key = `semprejoias_chat_read_${user?.id || "anon"}_${activeOperator?.id || "none"}`;
      return JSON.parse(localStorage.getItem(key) || "{}");
    } catch {
      return {};
    }
  }, [user?.id, activeOperator?.id]);

  const markChannelAsRead = useCallback((channelId) => {
    if (!channelId) return;
    try {
      const key = `semprejoias_chat_read_${user?.id || "anon"}_${activeOperator?.id || "none"}`;
      const current = JSON.parse(localStorage.getItem(key) || "{}");
      current[channelId] = new Date().toISOString();
      localStorage.setItem(key, JSON.stringify(current));
    } catch {}

    setUnreadByChannel((prev) => {
      if (!prev[channelId]) return prev;
      const next = { ...prev };
      delete next[channelId];
      return next;
    });
  }, [user?.id, activeOperator?.id]);

  const markAllChannelsAsRead = useCallback(() => {
    try {
      const key = `semprejoias_chat_read_${user?.id || "anon"}_${activeOperator?.id || "none"}`;
      const current = JSON.parse(localStorage.getItem(key) || "{}");
      const now = new Date().toISOString();
      [...channels, ...dms].forEach((c) => {
        current[c.id] = now;
      });
      localStorage.setItem(key, JSON.stringify(current));
    } catch {}

    setUnreadByChannel({});
  }, [user?.id, activeOperator?.id, channels, dms]);

  // --- 1. CARREGAR LISTA DE USUÁRIOS, OPERADORES E EQUIPES PARA MENÇÕES ---
  const loadDirectory = useCallback(async () => {
    try {
      const [uList, opList, eqList] = await Promise.all([
        pb.collection("users").getFullList({ sort: "name" }).catch(() => []),
        pb.collection("oficina_operadores").getFullList({ filter: "ativo = true", sort: "nome" }).catch(() => []),
        colaboradoresService.fetchEquipes().catch(() => []),
      ]);
      setUsersList(uList);
      setOperatorsList(opList);
      setTeamsList(eqList || []);
    } catch (e) {
      console.warn("[CommunicationContext] Erro ao carregar diretório:", e);
    }
  }, []);

  useEffect(() => {
    loadDirectory();
  }, [loadDirectory]);

  // --- 2. CARREGAR CANAIS E DMS ---
  const loadChannels = useCallback(async () => {
    if (!user) return;
    try {
      const { channels: chList, dms: dmList } = await chatService.getChannels({
        user,
        activeOperator,
        hasAccess,
      });
      setChannels(chList);
      setDms(dmList);

      // 1. Considera apenas canais e DMs que este usuário de fato participa
      const myChannelIds = new Set([...chList.map((c) => c.id), ...dmList.map((d) => d.id)]);

      // 2. Calcula mensagens não lidas consultando as mensagens mais recentes do sistema
      const lastReads = getLastReadTimestamps();
      const initialUnreads = {};

      try {
        const recentMsgs = await pb.collection("chat_messages").getList(1, 150, {
          sort: "-created",
          fields: "id,channel,created,sender_user,sender_operator",
        });

        recentMsgs.items.forEach((m) => {
          // Ignora mensagens de canais ou DMs de terceiros aos quais não pertenço
          if (!myChannelIds.has(m.channel)) return;

          const isFromMe =
            (user?.id && m.sender_user === user.id) ||
            (activeOperator?.id && m.sender_operator === activeOperator.id);
          if (isFromMe) return;

          // Se a gaveta estiver aberta na aba de chat olhando exatamente este canal, não conta como não lido
          const isCurrentActive =
            drawerOpenRef.current &&
            activeDrawerTabRef.current === "chat" &&
            activeChannelRef.current?.id === m.channel;
          if (isCurrentActive) return;

          const lastReadTime = lastReads[m.channel];
          if (!lastReadTime || new Date(m.created) > new Date(lastReadTime)) {
            initialUnreads[m.channel] = (initialUnreads[m.channel] || 0) + 1;
          }
        });
      } catch (err) {
        console.warn("Aviso ao buscar mensagens recentes para contagem de não lidas:", err);
      }

      setUnreadByChannel(initialUnreads);

      // Se nenhum canal ativo, seleciona o primeiro por padrão
      if (!activeChannelRef.current && chList.length > 0) {
        setActiveChannel(chList[0]);
      }
    } catch (e) {
      console.error("[CommunicationContext] Erro ao buscar canais:", e);
    }
  }, [user, activeOperator, hasAccess, getLastReadTimestamps]);

  // --- 3. CARREGAR MENSAGENS DO CANAL ATIVO ---
  const loadActiveMessages = useCallback(async (channelId, isBackground = false) => {
    if (!channelId) return;
    if (!isBackground) setLoadingMessages(true);
    try {
      const res = await chatService.getMessages(channelId, 1, 50);
      setMessages(res.items || []);
      markChannelAsRead(channelId);
    } catch (e) {
      console.error("[CommunicationContext] Erro ao buscar mensagens:", e);
    } finally {
      if (!isBackground) setLoadingMessages(false);
    }
  }, [markChannelAsRead]);

  useEffect(() => {
    if (activeChannel?.id) {
      loadActiveMessages(activeChannel.id, false);
      markChannelAsRead(activeChannel.id);
    }
  }, [activeChannel?.id, loadActiveMessages, markChannelAsRead]);

  // Marca canal ativo como lido quando a gaveta de chat é aberta
  useEffect(() => {
    if (drawerOpen && activeDrawerTab === "chat" && activeChannel?.id) {
      markChannelAsRead(activeChannel.id);
    }
  }, [drawerOpen, activeDrawerTab, activeChannel?.id, markChannelAsRead]);

  // --- 4. CARREGAR NOTIFICAÇÕES E CONTADORES ---
  const loadNotificationsAndCounts = useCallback(async () => {
    if (!user) return;
    try {
      const [notifs, unreadCount, pendingTasks] = await Promise.all([
        notificationService.getNotifications({
          userId: user.id,
          operatorId: activeOperator?.id,
          limit: 40,
        }),
        notificationService.getUnreadCount({
          userId: user.id,
          operatorId: activeOperator?.id,
        }),
        taskService.getPendingCount({
          userId: user.id,
          operatorId: activeOperator?.id,
          teamId: currentUserTeam,
          teamIds: currentUserTeams,
        }),
      ]);

      setNotifications(notifs);
      setUnreadNotifications(unreadCount);
      setPendingTasksCount(pendingTasks);
    } catch (e) {
      console.warn("[CommunicationContext] Erro ao carregar notificações:", e);
    }
  }, [user, activeOperator, currentUserTeam, currentUserTeams]);

  // --- 5. CARREGAR TAREFAS ---
  const loadTasks = useCallback(async () => {
    if (!user) return;
    try {
      const taskList = await taskService.getTasks({
        userId: user.id,
        operatorId: activeOperator?.id,
        teamId: currentUserTeam,
        teamIds: currentUserTeams,
      });
      setTasks(taskList);
    } catch (e) {
      console.warn("[CommunicationContext] Erro ao carregar tarefas:", e);
    }
  }, [user, activeOperator, currentUserTeam, currentUserTeams]);

  // Carrega times do colaborador logado ou do operador ativo na bancada
  useEffect(() => {
    const targetUserId = activeOperator?.colaborador || user?.id;
    if (!targetUserId) return;

    Promise.all([
      colaboradoresService.fetchEquipes().catch(() => []),
      pb.collection("colaboradores_config").getFullList({ filter: `colaborador = "${targetUserId}"` }).catch(() => []),
    ]).then(([allTeams, configs]) => {
      const myTeams = new Set();
      allTeams.forEach((t) => {
        const rawMembros = Array.isArray(t.membros)
          ? t.membros.map((m) => (typeof m === "object" ? m.id : m))
          : [];
        if (rawMembros.includes(targetUserId) || t.lider === targetUserId || t.vice_lider === targetUserId) {
          myTeams.add(t.id);
        }
      });
      if (configs.length > 0 && configs[0].equipe) {
        myTeams.add(configs[0].equipe);
      }

      const teamArray = Array.from(myTeams);
      setCurrentUserTeams(teamArray);
      setCurrentUserTeam(teamArray[0] || null);
    }).catch(() => {});
  }, [user, activeOperator]);

  // Recarrega tudo quando o usuário ou operador ativo muda
  useEffect(() => {
    if (user) {
      loadChannels();
      loadNotificationsAndCounts();
      loadTasks();
    }
  }, [user, activeOperator, loadChannels, loadNotificationsAndCounts, loadTasks]);

  // Polling suave a cada 20 segundos para garantir sincronia caso SSE seja desconectado
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      if (document.hasFocus()) {
        loadNotificationsAndCounts();
        if (activeChannelRef.current?.id) {
          loadActiveMessages(activeChannelRef.current.id, true);
        }
      }
    }, 20000);

    return () => clearInterval(interval);
  }, [user, loadNotificationsAndCounts, loadActiveMessages]);

  // --- 6. REALTIME SUBSCRIPTIONS (PocketBase SSE) ---
  useEffect(() => {
    if (!user) return;

    // Subscrição em chat_messages
    const unsubChat = pb.collection("chat_messages").subscribe("*", (e) => {
      if (e.action === "create") {
        const msg = e.record;
        const isDrawerOpen = drawerOpenRef.current;
        const isChatTab = activeDrawerTabRef.current === "chat";
        const isCurrentActive = isDrawerOpen && isChatTab && activeChannelRef.current?.id === msg.channel;

        const isFromMe =
          (user?.id && msg.sender_user === user.id) ||
          (activeOperator?.id && msg.sender_operator === activeOperator.id);

        if (isCurrentActive) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          markChannelAsRead(msg.channel);
        } else {
          if (!isFromMe) {
            setUnreadByChannel((prev) => ({
              ...prev,
              [msg.channel]: (prev[msg.channel] || 0) + 1,
            }));
          }
        }

        // Toca som de mensagem caso não seja enviada pelo próprio usuário
        if (!isFromMe) {
          soundNotificationService.playMessageSound();
        }

        // Se o canal desta mensagem recebida ainda não estiver carregado na memória, recarrega os canais
        const isKnownChannel = [...channelsRef.current, ...dmsRef.current].some((c) => c.id === msg.channel);
        if (!isKnownChannel) {
          loadChannels();
        }
      }
    }).catch(() => {});

    // Subscrição em chat_channels (para DMs e novos canais aparecerem instantaneamente para todos os membros)
    const unsubChannels = pb.collection("chat_channels").subscribe("*", () => {
      loadChannels();
    }).catch(() => {});

    // Subscrição em notifications
    const unsubNotif = pb.collection("notifications").subscribe("*", (e) => {
      const isForMe =
        (user?.id && e.record.recipient_user === user.id) ||
        (activeOperator?.id && e.record.recipient_operator === activeOperator.id);

      if (isForMe) {
        if (e.action === "create") {
          setNotifications((prev) => [e.record, ...prev]);
          setUnreadNotifications((prev) => prev + 1);

          // Alerta sonoro de notificação / tarefa
          if (e.record.priority === "urgente" || e.record.type === "urgent") {
            soundNotificationService.playUrgentSound();
          } else {
            soundNotificationService.playNotificationSound();
          }
        } else if (e.action === "update") {
          setNotifications((prev) => prev.map((n) => (n.id === e.record.id ? e.record : n)));
        } else if (e.action === "delete") {
          setNotifications((prev) => prev.filter((n) => n.id !== e.record.id));
        }
      }
    }).catch(() => {});

    // Subscrição em system_tasks para refletir conclusão coletiva e novas tarefas em tempo real
    const unsubTasks = pb.collection("system_tasks").subscribe("*", (e) => {
      if (e.action === "create") {
        const isAssignedToMyTeam =
          (currentUserTeam && (e.record.assigned_team === currentUserTeam || e.record.module_key === `team_${currentUserTeam}`)) ||
          (currentUserTeams.length > 0 && (currentUserTeams.includes(e.record.assigned_team) || currentUserTeams.some((tid) => e.record.module_key === `team_${tid}`)));

        const isForMe =
          (user?.id && (e.record.assigned_user === user.id || e.record.created_by_user === user.id)) ||
          (activeOperator?.id && e.record.assigned_operator === activeOperator.id) ||
          isAssignedToMyTeam;

        if (isForMe && e.record.created_by_user !== user?.id) {
          if (e.record.priority === "urgente") {
            soundNotificationService.playUrgentSound();
          } else {
            soundNotificationService.playNotificationSound();
          }
        }
      }
      loadTasks();
      loadNotificationsAndCounts();
    }).catch(() => {});

    return () => {
      pb.collection("chat_messages").unsubscribe("*").catch(() => {});
      pb.collection("chat_channels").unsubscribe("*").catch(() => {});
      pb.collection("notifications").unsubscribe("*").catch(() => {});
      pb.collection("system_tasks").unsubscribe("*").catch(() => {});
    };
  }, [user, activeOperator, loadChannels, loadTasks, loadNotificationsAndCounts]);

  // --- 7. AÇÕES DE CHAT ---
  const sendMessage = async ({
    content,
    files = [],
    replyToId = null,
    mentionsUsers = [],
    mentionsOperators = [],
    mentionsTeams = [],
  }) => {
    if (!activeChannel) return;
    try {
      const record = await chatService.sendMessage({
        channelId: activeChannel.id,
        user,
        activeOperator,
        content,
        files,
        replyToId,
        mentionsUsers,
        mentionsOperators,
        channelInfo: activeChannel,
      });

      setMessages((prev) => {
        if (prev.some((m) => m.id === record.id)) return prev;
        return [...prev, record];
      });

      // --- DISPARO DE NOTIFICAÇÕES PARA MEMBROS DE EQUIPES MENCIONADAS COM @ ---
      if (mentionsTeams && mentionsTeams.length > 0) {
        const senderName = activeOperator
          ? `${activeOperator.nome} (Operador)`
          : user?.name || user?.username || "Alguém";

        Promise.all([
          colaboradoresService.fetchEquipes().catch(() => []),
          pb.collection("colaboradores_config").getFullList().catch(() => []),
        ]).then(([allTeams, configs]) => {
          mentionsTeams.forEach((team) => {
            const currentTeamData = allTeams.find((t) => t.id === team.id) || team;
            const rawMembros = Array.isArray(currentTeamData.membros)
              ? currentTeamData.membros.map((m) => (typeof m === "object" ? m.id : m))
              : [];
            const legacyMembers = configs.filter((c) => c.equipe === team.id).map((c) => c.colaborador);
            const allMemberIds = new Set([...rawMembros, ...legacyMembers]);

            allMemberIds.forEach((memberUserId) => {
              if (memberUserId && memberUserId !== user?.id) {
                notificationService.createNotification({
                  recipient_user: memberUserId,
                  type: "team_mention",
                  title: `📢 Sua equipe (${team.nome}) foi mencionada`,
                  message: `${senderName} mencionou sua equipe em #${activeChannel.display_name || activeChannel.name}: "${content.slice(0, 90)}"`,
                  link_tab: "chat",
                  link_id: activeChannel.id,
                }).catch(() => {});
              }
            });
          });
        }).catch(() => {});
      }

      // --- DISPARO DO ASSISTENTE DE IA COMPANION ---
      const isAICall =
        activeChannel.type === "ai" ||
        content.toLowerCase().includes("@ia") ||
        content.toLowerCase().includes("@assistente");

      if (isAICall) {
        setIsAiThinking(true);
        const channelId = activeChannel.id;

        setTimeout(async () => {
          try {
            const aiReply = await aiAssistantService.ask({
              query: content,
              userContext: { user, hasAccess, activeOperator },
              preferredProviderId: selectedAiProviderRef.current,
            });

            if (aiReply && aiReply.text) {
              const aiRecord = await pb.collection("chat_messages").create({
                channel: channelId,
                sender_name: "Assistente Sempre Joias ✨",
                sender_color: "#6366f1",
                content: aiReply.text,
                referenced_orders: chatService.parseMessageContent(aiReply.text).orders,
              });

              setMessages((prev) => {
                if (prev.some((m) => m.id === aiRecord.id)) return prev;
                return [...prev, aiRecord];
              });
            }
          } catch (aiErr) {
            console.error("Erro na resposta da IA:", aiErr);
            const errorRecord = await pb.collection("chat_messages").create({
              channel: channelId,
              sender_name: "Assistente Sempre Joias ✨",
              sender_color: "#ef4444",
              content: `⚠️ Não foi possível consultar o assistente: ${aiErr.message || "Verifique as configurações de IA."}`,
            }).catch(() => null);

            if (errorRecord) {
              setMessages((prev) => [...prev, errorRecord]);
            }
          } finally {
            setIsAiThinking(false);
          }
        }, 150);
      }

      return record;
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      throw error;
    }
  };

  const clearCurrentChannelMessages = async () => {
    if (!activeChannel?.id) return;
    setMessages([]);
    try {
      await chatService.clearChannelMessages(activeChannel.id);
    } catch (e) {
      console.warn("[CommunicationContext] Erro ao limpar mensagens do canal:", e);
    }
  };

  const startAIChat = async () => {
    try {
      const aiChannel = await chatService.getOrCreateAIChannel({
        currentUser: user,
        currentOperator: activeOperator,
      });

      setActiveChannel(aiChannel);
      markChannelAsRead(aiChannel.id);
      setActiveDrawerTab("chat");
      setDrawerOpen(true);
    } catch (e) {
      console.error("Erro ao iniciar chat com a IA:", e);
    }
  };

  // Se trocar de operador enquanto o assistente estiver aberto, migra para a conversa isolada do novo operador
  useEffect(() => {
    if (activeChannel?.type === "ai" && user) {
      chatService.getOrCreateAIChannel({ currentUser: user, currentOperator: activeOperator })
        .then((newAiCh) => {
          setActiveChannel(newAiCh);
          markChannelAsRead(newAiCh.id);
        })
        .catch(() => {});
    }
  }, [activeOperator]);

  const startDM = async ({ targetUser, targetOperator }) => {
    try {
      const dmChannel = await chatService.getOrCreateDM({
        currentUser: user,
        currentOperator: activeOperator,
        targetUser,
        targetOperator,
      });

      setDms((prev) => {
        if (prev.some((d) => d.id === dmChannel.id)) return prev;
        return [dmChannel, ...prev];
      });

      setActiveChannel(dmChannel);
      setActiveDrawerTab("chat");
      setDrawerOpen(true);
    } catch (e) {
      console.error("Erro ao iniciar DM:", e);
    }
  };

  const selectChannelById = async (channelId) => {
    if (!channelId) return;
    const existing = [...channels, ...dms].find((c) => c.id === channelId);
    if (existing) {
      setActiveChannel(existing);
      setActiveDrawerTab("chat");
      setDrawerOpen(true);
      return;
    }

    try {
      const ch = await pb.collection("chat_channels").getOne(channelId, {
        expand: "members_users,members_operators",
      });
      if (ch) {
        if (ch.type === "direct" || ch.type === "ai") {
          setDms((prev) => (prev.some((d) => d.id === ch.id) ? prev : [ch, ...prev]));
        } else {
          setChannels((prev) => (prev.some((c) => c.id === ch.id) ? prev : [ch, ...prev]));
        }
        setActiveChannel(ch);
        setActiveDrawerTab("chat");
        setDrawerOpen(true);
      }
    } catch (e) {
      console.warn("Erro ao selecionar canal por ID:", e);
    }
  };

  // --- 8. AÇÕES DE NOTIFICAÇÃO ---
  const markNotificationAsRead = async (id) => {
    await notificationService.markAsRead(id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadNotifications((prev) => Math.max(0, prev - 1));
  };

  const markAllNotificationsAsRead = async () => {
    await notificationService.markAllAsRead({
      userId: user?.id,
      operatorId: activeOperator?.id,
    });
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadNotifications(0);
  };

  // --- 9. AÇÕES DE TAREFA ---
  const createTask = async (taskData) => {
    const record = await taskService.createTask({
      ...taskData,
      created_by_user: user,
      created_by_operator: activeOperator,
    });
    setTasks((prev) => [record, ...prev]);
    loadNotificationsAndCounts();
    return record;
  };

  const updateTaskStatus = async (taskId, status) => {
    const updated = await taskService.updateStatus(taskId, status, user);
    setTasks((prev) =>
      prev.map((t) =>
        t.id === taskId
          ? {
              ...t,
              status,
              completed_at: updated?.completed_at,
              completed_by_name: updated?.completed_by_name || user?.name,
            }
          : t
      )
    );
    loadNotificationsAndCounts();
  };

  const deleteTask = async (taskId) => {
    await taskService.deleteTask(taskId);
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    loadNotificationsAndCounts();
  };

  // --- 10. ABERTURA DE MODAIS ---
  const openDrawer = (tab = "chat") => {
    setActiveDrawerTab(tab);
    setDrawerOpen(true);
    if (tab === "chat" && activeChannelRef.current?.id) {
      markChannelAsRead(activeChannelRef.current.id);
    }
  };

  const closeDrawer = () => setDrawerOpen(false);

  const openQuickOrder = (orderNum) => {
    if (orderNum) {
      setQuickOrderNumber(String(orderNum).replace(/[[\]#]/g, "").trim());
    }
  };
  const closeQuickOrder = () => setQuickOrderNumber(null);

  const openQuickProduct = (sku) => {
    if (sku) {
      setQuickProductSku(String(sku).replace(/^sku[.:]/i, "").replace(/[[\]]/g, "").trim());
    }
  };
  const closeQuickProduct = () => setQuickProductSku(null);

  const openLightbox = (url, name = "Imagem") => setLightboxData({ url, name });
  const closeLightbox = () => setLightboxData(null);

  const createChannel = async (channelData) => {
    const record = await chatService.createChannel(channelData);
    await loadChannels();
    setActiveChannel(record);
    return record;
  };

  const seedChannels = async () => {
    await chatService.seedDefaultChannels();
    await loadChannels();
  };

  return (
    <CommunicationContext.Provider
      value={{
        // UI
        drawerOpen,
        openDrawer,
        closeDrawer,
        activeDrawerTab,
        setActiveDrawerTab,
        quickOrderNumber,
        openQuickOrder,
        closeQuickOrder,
        quickProductSku,
        openQuickProduct,
        closeQuickProduct,
        lightboxData,
        openLightbox,
        closeLightbox,
        newTaskModalOpen,
        setNewTaskModalOpen,

        // Contadores
        unreadNotifications,
        unreadChatCount,
        unreadByChannel,
        markChannelAsRead,
        markAllChannelsAsRead,
        pendingTasksCount,

        // Dados
        channels,
        dms,
        activeChannel,
        setActiveChannel,
        messages,
        loadingMessages,
        isAiThinking,
        selectedAiProviderId,
        setSelectedAiProviderId,
        notifications,
        tasks,
        usersList,
        operatorsList,
        teamsList,
        currentUserTeam,
        currentUserTeams,

        // Som & Preferências
        soundEnabled,
        toggleSound,
        testSound: (type = "message") => {
          if (type === "urgent") soundNotificationService.playUrgentSound();
          else if (type === "notif") soundNotificationService.playNotificationSound();
          else soundNotificationService.playMessageSound();
        },

        // Ações
        sendMessage,
        clearCurrentChannelMessages,
        startDM,
        selectChannelById,
        startAIChat,
        createChannel,
        seedChannels,
        markNotificationAsRead,
        markAllNotificationsAsRead,
        markChannelAsRead,
        createTask,
        updateTaskStatus,
        deleteTask,
        refreshAll: () => {
          loadChannels();
          loadNotificationsAndCounts();
          loadTasks();
        },
      }}
    >
      {children}
    </CommunicationContext.Provider>
  );
}
